import React, { useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  FormControlLabel,
  IconButton,
  Link,
  Popover,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import EditOffIcon from '@mui/icons-material/EditOff'
import VisibilityIcon from '@mui/icons-material/Visibility'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import ShareIcon from '@mui/icons-material/Share'
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import PaletteIcon from '@mui/icons-material/Palette'
import useRouter from '../hooks/useRouter'
import useAccount from '../hooks/useAccount'
import useDashboards from '../hooks/useDashboards'
import useBrandThemes from '../hooks/useBrandThemes'
import useSnackbar from '../hooks/useSnackbar'
import DeleteConfirmWindow from '../components/system/DeleteConfirmWindow'
import DashboardSharePopover from '../components/dashboard/DashboardSharePopover'
import ThemeManagerDialog from '../components/brand-palette/ThemeManagerDialog'
import PageHeader from '../components/system/PageHeader'
import PageAgentButton from '../agent/components/PageAgentButton'
import PageAgentDrawer from '../agent/components/PageAgentDrawer'
import ActionButton from '../components/system/ActionButton'
import TierSection from '../components/dashboard/TierSection'
import { Dashboard } from '../types/gotypes'
import { canPromoteDashboard } from '../utils/roleChecks'
import { noDeleteDashboardReason, noEditPermissionReason, canOverwriteDashboard } from '../utils/dashboardOwnership'
import DisabledReason from '../components/system/DisabledReason'
import useWhoToAsk from '../hooks/useWhoToAsk'
import type { WhoToAsk } from '../utils/permissionReason'
import { DATAVIZ_TYPES, dashboardTypeMeta } from '../utils/dashboardType'
import { useVoiceRegistration } from '../voice/useVoiceRegistration'
import { buildVoiceTools, voiceCollection } from '../voice/buildVoiceTools'
import { VoiceUITool } from '../voice/types'
import { useDisplayNames } from '../utils/displayNames'

// No seconds (#1179): nobody reads them, and the shorter value is narrower.
const formatDate = (timestamp: number) =>
  new Date(timestamp * 1000).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })

/** Truncating cell text: ellipsis with the full value in a hover tooltip
 *  (same maxWidth/ellipsis pattern the app uses for long table names). */
const TruncatedText: React.FC<{
  value: string
  emphasis?: 'primary' | 'secondary'
}> = ({ value, emphasis = 'secondary' }) => {
  if (!value) return <Typography variant="body2" color="text.disabled">—</Typography>
  return (
    <Tooltip title={value} enterDelay={500}>
      <Typography
        variant="body2"
        noWrap
        sx={
          emphasis === 'primary'
            ? { fontWeight: 600, minWidth: 0 }
            : { color: 'text.secondary', fontSize: 13, minWidth: 0 }
        }
      >
        {value}
      </Typography>
    </Tooltip>
  )
}

/** Type leads the row (#1179): icon + short label, styled per type, so exec
 *  reports, analysis dashboards and web apps read apart at a glance. */
const TypeCellContent: React.FC<{ type: string }> = ({ type }) => {
  const meta = dashboardTypeMeta(type)
  const Icon = meta.Icon
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
      <Icon fontSize="small" sx={{ color: meta.color, flexShrink: 0 }} />
      <Typography variant="body2" noWrap sx={{ color: meta.color, fontSize: 13, fontWeight: 500 }}>
        {meta.label}
      </Typography>
    </Box>
  )
}

/** Job column: the job's display name, never the slug. Multi-job is a
 *  PROPERTY (#1162), so a span reads as a "Multi" chip whose hover names the
 *  jobs. No job at all reads "—". */
const JobCellContent: React.FC<{ job?: string; jobs?: string[]; resolveJobName: (slug: string) => string }> = ({ job, jobs, resolveJobName }) => {
  if (jobs && jobs.length > 1) {
    return (
      <Tooltip title={jobs.map(resolveJobName).join(' · ')}>
        <Chip size="small" variant="outlined" label="Multi" sx={{ height: 20, fontSize: 11 }} />
      </Tooltip>
    )
  }
  const slug = jobs?.length === 1 ? jobs[0] : job
  return (
    <Typography variant="body2" noWrap sx={{ fontSize: 13 }}>
      {slug ? resolveJobName(slug) : '—'}
    </Typography>
  )
}

interface DashboardRowProps {
  dashboard: Dashboard
  canOverwrite: boolean
  whoToAsk: WhoToAsk
  canPromote: boolean
  isNameEditing: boolean
  onStartEditName: () => void
  onCommitName: (value: string) => void
  onCancelEdit: () => void
  onEdit: () => void
  onView: () => void
  onDelete: () => void
  onShareClick: (el: HTMLElement) => void
  onInfoClick: (el: HTMLElement) => void
  resolveJobName: (slug: string) => string
  onPromote: () => void
  onDemote: () => void
  onDuplicate: () => void
  isDuplicating: boolean
}

/** One DataViz row — shared by the company-level, my, and colleague-group
 *  tables. Columns: Type, Job, Name, Owner, Created,
 *  Actions. Clicking the row itself opens the item; interactive cells
 *  stop the click. */
const DashboardRow: React.FC<DashboardRowProps> = ({
  dashboard,
  canOverwrite,
  whoToAsk,
  canPromote,
  isNameEditing,
  onStartEditName,
  onCommitName,
  onCancelEdit,
  onEdit,
  onView,
  onDelete,
  onShareClick,
  onInfoClick,
  resolveJobName,
  onPromote,
  onDemote,
  onDuplicate,
  isDuplicating,
}) => {
  const name = dashboard.config?.name || 'Unnamed Dashboard'
  // A web app or a video has no editor: it opens in the viewer only.
  const hasEditor = dashboardTypeMeta(dashboard.type).opens === 'editor'
  const isShared = Boolean(dashboard.share_enabled)

  const [nameDraft, setNameDraft] = useState(name)

  return (
    <TableRow hover onClick={onView} sx={{ cursor: 'pointer' }}>
      <TableCell sx={{ overflow: 'hidden' }}>
        <TypeCellContent type={dashboard.type} />
      </TableCell>
      <TableCell sx={{ overflow: 'hidden' }}>
        <JobCellContent job={dashboard.job} jobs={dashboard.jobs} resolveJobName={resolveJobName} />
      </TableCell>
      <TableCell sx={{ overflow: 'hidden' }} onClick={(e) => { if (canOverwrite || isNameEditing) e.stopPropagation() }}>
        {isNameEditing ? (
          <TextField
            size="small"
            fullWidth
            autoFocus
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            onBlur={() => onCommitName(nameDraft)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onCommitName(nameDraft)
              if (e.key === 'Escape') { setNameDraft(name); onCancelEdit() }
            }}
          />
        ) : (
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: canOverwrite ? 'text' : 'pointer', minWidth: 0 }}
            onClick={() => { if (canOverwrite) { setNameDraft(name); onStartEditName() } }}
          >
            <TruncatedText value={name} emphasis="primary" />
            {/* #1578 item 15: the web app was saved in the story board editor
                since it was last published, so DataViz still shows the older version. */}
            {dashboard.webapp_unpublished && (
              <Tooltip title="This web app was saved in the story board editor since it was last published. Open the editor and press Publish to update it here.">
                <Chip
                  label="Unpublished changes"
                  size="small"
                  color="warning"
                  variant="outlined"
                  data-testid="dashboard-unpublished-changes"
                  sx={{ height: 20, fontSize: 11, flexShrink: 0 }}
                />
              </Tooltip>
            )}
            {dashboard.promoted_at && (
              <Tooltip title={`Promoted to company-level by ${dashboard.promoted_by || 'a manager'}`}>
                <StarIcon fontSize="small" sx={{ color: 'warning.main', flexShrink: 0 }} />
              </Tooltip>
            )}
          </Box>
        )}
      </TableCell>
      {/* #1257: Owner is `user` — the field canOverwriteDashboard tests and the
          tiers group by — never `created_by`, which names whoever made it and
          diverges on a fork. It repeats the section header on My / colleagues'
          rows; it is here on every tier so the grids stay aligned, and it is
          the only place Company-level names who to ask. */}
      <TableCell sx={{ overflow: 'hidden' }}>
        <Box data-testid="dashboard-owner" sx={{ minWidth: 0 }}>
          <TruncatedText value={dashboard.user} />
        </Box>
      </TableCell>
      <TableCell sx={{ overflow: 'hidden' }}>
        <Box data-testid="dashboard-created" sx={{ minWidth: 0 }}>
          <TruncatedText value={dashboard.created_at ? formatDate(dashboard.created_at) : ''} />
        </Box>
      </TableCell>
      {/* #1179 (Matt, 7 Sep): the INFO column header said nothing; the info
          icon now lives in the actions area beside the star, and the Type
          column keeps the width the header freed. */}
      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
        <Tooltip title="Details">
          <IconButton onClick={(e) => onInfoClick(e.currentTarget)} size="small" aria-label={`Details for ${name}`}>
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {canPromote && (
          <Tooltip title={dashboard.promoted_at ? 'Demote from company-level' : 'Promote to company-level'}>
            <IconButton
              onClick={dashboard.promoted_at ? onDemote : onPromote}
              size="small"
              aria-label={dashboard.promoted_at ? `Demote ${name} from company-level` : `Promote ${name} to company-level`}
            >
              {dashboard.promoted_at ? <StarIcon fontSize="small" sx={{ color: 'warning.main' }} /> : <StarBorderIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
        {/* Share state lives on the icon (#1179): coloured when shared, so the
            list still says which items are shared without a Sharing column. */}
        <Tooltip title={isShared ? `Shared — ${dashboard.share_access_level === 'password' ? 'password' : 'organization'} access` : 'Not shared'}>
          <IconButton onClick={(e) => onShareClick(e.currentTarget)} size="small" aria-label={isShared ? 'Sharing (shared)' : 'Sharing (not shared)'}>
            {isShared
              ? <ShareIcon fontSize="small" sx={{ color: 'info.main' }} />
              : <ShareOutlinedIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Duplicate to My DataViz">
          <span>
            <IconButton onClick={onDuplicate} size="small" disabled={isDuplicating} aria-label="Duplicate to My DataViz">
              {isDuplicating ? <CircularProgress size={16} /> : <ContentCopyIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        {/* #1262: the pencil follows the save-in-place rule. Anyone who can't
            save this item in place gets a struck-through, disabled pencil and
            a disabled Delete, each saying why and who to ask (Adam, 8 Oct:
            never hide an action). Duplicate above stays live as the route to
            a copy of their own. */}
        {hasEditor && (canOverwrite ? (
          <Tooltip title="Edit">
            <IconButton onClick={onEdit} size="small" aria-label={`Edit ${name}`}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <DisabledReason reason={noEditPermissionReason(whoToAsk, dashboard)}>
            <IconButton size="small" aria-label={`Edit ${name}`}>
              <EditOffIcon fontSize="small" color="disabled" />
            </IconButton>
          </DisabledReason>
        ))}
        <Tooltip title="View">
          <IconButton onClick={onView} size="small" aria-label={`View ${name}`}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {canOverwrite ? (
          <Tooltip title="Delete">
            <IconButton onClick={onDelete} size="small" aria-label={`Delete ${name}`}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <DisabledReason reason={noDeleteDashboardReason(whoToAsk, dashboard)}>
            <IconButton size="small" aria-label={`Delete ${name}`}>
              <DeleteIcon fontSize="small" color="disabled" />
            </IconButton>
          </DisabledReason>
        )}
      </TableCell>
    </TableRow>
  )
}

// Shared column grid (#962 Obs G, cut to four columns by #1179, Owner added
// back by #1257): every tier table renders the same <colgroup> and uses
// table-layout: fixed, so column widths come from these definitions rather
// than per-table content — columns line up vertically across the
// Company-level / My DataViz / colleague sections. Name got the width freed by
// the dropped Description, Job, Created by, Last modified by and Sharing
// columns (those fields live in the Info popover) and gives some of it back
// to Owner.
const TIER_COLUMNS: { label: string; width: string }[] = [
  // #1179 (Matt, 7 Sep): Type widened; the Info column is gone (its icon moved into Actions), and its
  // width plus a little from elsewhere goes to Type.
  // #1578 item 14: Created is back as a column, between Owner and Actions.
  { label: 'Type', width: '14%' },
  // Job display name, between Type and Name (#1578); `Multi` chip for a span.
  { label: 'Job', width: '12%' },
  { label: 'Name', width: '19%' },
  { label: 'Owner', width: '16%' },
  // Wide enough for "29/09/2026, 00:50" at a 1600px window.
  { label: 'Created', width: '15%' },
  { label: 'Actions', width: '24%' },
]

interface TierTableProps {
  dashboards: Dashboard[]
  emptyLabel: string
  rowProps: (dashboard: Dashboard) => Omit<DashboardRowProps, 'dashboard'>
}

const TierTable: React.FC<TierTableProps> = ({ dashboards, emptyLabel, rowProps }) => (
  <TableContainer>
    <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 760 }}>
      <colgroup>
        {TIER_COLUMNS.map((col) => (
          <col key={col.label} style={{ width: col.width }} />
        ))}
      </colgroup>
      <TableHead>
        <TableRow>
          {TIER_COLUMNS.map((col) => (
            <TableCell
              key={col.label}
              align={col.label === 'Actions' ? 'right' : undefined}
              sx={{
                fontSize: 11,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: 'text.secondary',
                whiteSpace: 'nowrap',
              }}
            >
              {col.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {dashboards.length === 0 ? (
          <TableRow>
            <TableCell colSpan={TIER_COLUMNS.length} align="center" sx={{ py: 3 }}>
              <Typography variant="body2" color="text.secondary">{emptyLabel}</Typography>
            </TableCell>
          </TableRow>
        ) : (
          dashboards.map((d) => <DashboardRow key={d.id} dashboard={d} {...rowProps(d)} />)
        )}
      </TableBody>
    </Table>
  </TableContainer>
)

type TierKey = 'company' | 'mine' | 'colleagues'

const TIER_OPEN_STORAGE_KEY = 'dataviz-tiers-open'

// Per-user open/closed memory for the three tiers (#1179). Stored explicitly
// only once the user toggles; before that, a tier defaults open when it has
// items and collapsed when empty.
const loadTierOpenState = (): Partial<Record<TierKey, boolean>> => {
  try {
    const raw = localStorage.getItem(TIER_OPEN_STORAGE_KEY)
    return raw ? (JSON.parse(raw) as Partial<Record<TierKey, boolean>>) : {}
  } catch {
    return {}
  }
}

const Dashboards: React.FC = () => {
  const router = useRouter()
  const account = useAccount()
  const snackbar = useSnackbar()
  const [deleteTarget, setDeleteTarget] = useState<Dashboard | null>(null)
  // The DataViz helper (Douglas plan T15).
  const [helperOpen, setHelperOpen] = useState(false)
  const [sharePopover, setSharePopover] = useState<{ anchorEl: HTMLElement; dashboard: Dashboard } | null>(null)
  const [infoPopover, setInfoPopover] = useState<{ anchorEl: HTMLElement; dashboardId: string } | null>(null)
  const [editingNameId, setEditingNameId] = useState<string | null>(null)
  const [expandedColleagues, setExpandedColleagues] = useState<Set<string>>(new Set())
  const [colleagueFilter, setColleagueFilter] = useState('')
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null)
  const [themeManagerOpen, setThemeManagerOpen] = useState(false)
  const [tierOpenOverrides, setTierOpenOverrides] = useState<Partial<Record<TierKey, boolean>>>(loadTierOpenState)

  const customer = router.params.customer || account.customer?.name || ''
  const userEmail = account.user?.session_info?.email || ''
  const userCanPromote = canPromoteDashboard(account.user?.role_permissions)
  const whoToAsk = useWhoToAsk(customer)
  // #1179: resolve a job slug to its display name for the UI (never show the
  // slug). Source is the session's job list for the current customer.
  const jobDisplayNames = useMemo(() => {
    const map: Record<string, string> = {}
    const custs = account.user?.session_info?.sessionCusts || []
    for (const c of custs) {
      if (customer && c.name !== customer) continue
      for (const j of c.sessionJobs || []) {
        if (j.name) map[j.name] = j.displayName || j.name
      }
    }
    return map
  }, [account.user, customer])
  const resolveJobName = (slug: string) => jobDisplayNames[slug] || slug
  // #1266: everyone gets Edit Themes — they can create their own and copy a
  // colleague's; what each row allows is decided inside by canEditBrandTheme.

  // Job scoping (#962) uses the same global Customer/Job pills every other
  // page (Tables included) shows in the top header — not a page-local
  // picker. Switching jobs there calls account.setJobID(), which already
  // persists the last customer+job selection app-wide (see contexts/
  // account.tsx's loadLastCustomerJob/saveLastCustomerJob), so there's no
  // separate "remember the last job for this page" concern to handle here.
  const selectedJob = account.job?.name || ''
  // #1179: the header shows the job's display name, never the route slug —
  // the same standing rule the customer name already follows elsewhere.
  const jobDisplayName = account.job?.displayName || selectedJob

  // #1162: the job selection is a FILTER, not a container. "All DataViz"
  // widens the list to every job of the customer. This is a PAGE-LOCAL
  // scope (persisted) rather than an entry in the global job picker — that
  // picker also drives Tables and Files, where "all jobs" has no meaning.
  const [scopeAll, setScopeAll] = useState(() => {
    try { return localStorage.getItem('dataviz-scope-all') === 'true' } catch { return false }
  })
  const handleScopeChange = (all: boolean) => {
    setScopeAll(all)
    try { localStorage.setItem('dataviz-scope-all', String(all)) } catch { /* per-session only */ }
  }

  // The type filter narrows every tier and its count. Remembered per browser.
  const [typeFilter, setTypeFilter] = useState<string>(() => {
    try {
      const saved = localStorage.getItem('dataviz-type-filter')
      return saved && DATAVIZ_TYPES.some(t => t.id === saved) ? saved : 'all'
    } catch { return 'all' }
  })
  const handleTypeFilterChange = (next: string | null) => {
    if (!next) return
    setTypeFilter(next)
    try { localStorage.setItem('dataviz-type-filter', next) } catch { /* per-session only */ }
  }

  const {
    dashboards,
    listIsLoading,
    listIsError,
    listError,
    updateDashboard,
    deleteDashboard,
    deleteIsLoading,
    shareDashboard,
    shareIsLoading,
    disableShare,
    promoteDashboard,
    demoteDashboard,
    forkDashboard,
  } = useDashboards({ customer, job: scopeAll ? undefined : selectedJob, allJobs: scopeAll })

  const {
    themes: brandThemes,
    createTheme,
    updateTheme,
    deleteTheme,
    promoteTheme,
    demoteTheme,
    loadThemeHistory,
    isLoading: themesIsLoading,
  } = useBrandThemes(customer)

  // Three-tier ownership grouping, mirroring Tables' exec/my/colleagues model
  // (#962). Tiers are mutually exclusive: a promoted dashboard always shows
  // as company-level regardless of who owns it.
  const { companyLevel, mine, colleagueGroups } = useMemo(() => {
    const companyLevel: Dashboard[] = []
    const mine: Dashboard[] = []
    const byColleague = new Map<string, Dashboard[]>()

    for (const d of dashboards) {
      if (typeFilter !== 'all' && dashboardTypeMeta(d.type).id !== typeFilter) continue
      if (d.promoted_at) {
        companyLevel.push(d)
      } else if (d.user === userEmail) {
        mine.push(d)
      } else {
        const list = byColleague.get(d.user) || []
        list.push(d)
        byColleague.set(d.user, list)
      }
    }

    const colleagueGroups = Array.from(byColleague.entries())
      .map(([email, items]) => ({ email, items }))
      .sort((a, b) => a.email.localeCompare(b.email))

    return { companyLevel, mine, colleagueGroups }
  }, [dashboards, userEmail, typeFilter])

  const colleagueCount = colleagueGroups.reduce((n, g) => n + g.items.length, 0)

  // Open state per tier: an explicit user toggle (persisted) wins; otherwise
  // a tier with items starts open and an empty tier starts collapsed.
  const tierCounts: Record<TierKey, number> = { company: companyLevel.length, mine: mine.length, colleagues: colleagueCount }
  const isTierOpen = (tier: TierKey) => tierOpenOverrides[tier] ?? tierCounts[tier] > 0

  const toggleTier = (tier: TierKey) => {
    setTierOpenOverrides((prev) => {
      const next = { ...prev, [tier]: !isTierOpen(tier) }
      try { localStorage.setItem(TIER_OPEN_STORAGE_KEY, JSON.stringify(next)) } catch { /* storage unavailable */ }
      return next
    })
  }

  const filteredColleagueGroups = useMemo(() => {
    const q = colleagueFilter.trim().toLowerCase()
    if (!q) return colleagueGroups
    return colleagueGroups
      .map((group) => ({
        ...group,
        items: group.email.toLowerCase().includes(q)
          ? group.items
          : group.items.filter((d) => (d.config?.name || '').toLowerCase().includes(q)),
      }))
      .filter((group) => group.items.length > 0)
  }, [colleagueGroups, colleagueFilter])

  const toggleColleague = (email: string) => {
    setExpandedColleagues((prev) => {
      const next = new Set(prev)
      if (next.has(email)) next.delete(email)
      else next.add(email)
      return next
    })
  }

  // #971 Phase 1: "+ New Dashboard" now lands on the creation screen (build
  // it yourself / start from a template / agent-assisted), not directly in
  // the editor. All three paths converge on the layout builder, which hands
  // off to dashboard-editor once the user clicks "Create Dashboard".
  const handleAddClick = () => {
    if (!customer) return
    if (!selectedJob) return
    router.navigate('dashboard-create', { customer })
  }

  const handleEditClick = (dashboard: Dashboard) => {
    if (!customer) return
    router.navigate('dashboard-editor', { customer, dashboardId: dashboard.id })
  }

  const handleViewClick = (dashboard: Dashboard) => {
    if (!customer) return
    router.navigate('dashboard-view', { customer, dashboardId: dashboard.id })
  }

  const handleDeleteClick = (dashboard: Dashboard) => setDeleteTarget(dashboard)

  const handleConfirmDelete = () => {
    if (!deleteTarget || deleteIsLoading) return
    deleteDashboard(deleteTarget.id)
    setDeleteTarget(null)
  }

  const handleCancelDelete = () => {
    if (deleteIsLoading) return
    setDeleteTarget(null)
  }

  const handleCommitName = (dashboard: Dashboard, value: string) => {
    setEditingNameId(null)
    const trimmed = value.trim()
    if (!dashboard.config || !trimmed || trimmed === dashboard.config.name) return
    // #1274: a refused rename (name clash — the hook has toasted the owner)
    // reopens the field with the draft still in it, so the user can choose
    // another name rather than retype.
    void Promise.resolve(updateDashboard(dashboard.id, { ...dashboard.config, name: trimmed }))
      .catch(() => setEditingNameId(dashboard.id))
  }

  const handleCommitDescription = (dashboard: Dashboard, value: string) => {
    const trimmed = value.trim()
    if (!dashboard.config || trimmed === (dashboard.config.description || '')) return
    updateDashboard(dashboard.id, { ...dashboard.config, description: trimmed })
  }

  // Duplicate (per #967's homepage row action, distinct from that issue's
  // bigger in-editor copy/paste): clones any visible dashboard — own,
  // colleague's, or company-level — into the current user's My DataViz
  // for the same job. Reuses the same fork endpoint the editor's clone-on-
  // save flow uses. The copy opens straight away (Adam, 8 Oct): the editor
  // for a dashboard, the viewer for a web app or a video, which have no editor here.
  const handleDuplicate = async (dashboard: Dashboard) => {
    setDuplicatingId(dashboard.id)
    try {
      const forked = await forkDashboard(dashboard.id)
      if (forked) {
        snackbar.success(`Duplicated to My DataViz as "${forked.config?.name || forked.id}"`)
        if (dashboardTypeMeta(forked.type).opens === 'view') handleViewClick(forked)
        else handleEditClick(forked)
      }
    } finally {
      setDuplicatingId(null)
    }
  }

  const rowPropsFor = (dashboard: Dashboard): Omit<DashboardRowProps, 'dashboard'> => {
    const canOverwrite = canOverwriteDashboard(dashboard, userEmail, userCanPromote)
    return {
      canOverwrite,
      whoToAsk,
      canPromote: userCanPromote,
      isNameEditing: editingNameId === dashboard.id,
      onStartEditName: () => setEditingNameId(dashboard.id),
      onCommitName: (value) => handleCommitName(dashboard, value),
      onCancelEdit: () => setEditingNameId(null),
      onEdit: () => handleEditClick(dashboard),
      onView: () => handleViewClick(dashboard),
      onDelete: () => handleDeleteClick(dashboard),
      onShareClick: (el) => setSharePopover({ anchorEl: el, dashboard }),
      onInfoClick: (el) => setInfoPopover({ anchorEl: el, dashboardId: dashboard.id }),
      resolveJobName,
      onPromote: () => promoteDashboard(dashboard.id),
      onDemote: () => demoteDashboard(dashboard.id),
      onDuplicate: () => handleDuplicate(dashboard),
      isDuplicating: duplicatingId === dashboard.id,
    }
  }

  // Confirm-gated delete: kept separate from the voiceCollection actions
  // below so the description/JSON reply can spell out the destructive
  // confirm-first flow (mirrors the confirm-first pattern used for
  // destructive/consequential server-side voice tools). It resolves the
  // dashboard by id from find_dashboard, same as the other item actions.
  const deleteDashboardTool: VoiceUITool = {
    name: 'delete_dashboard',
    description:
      'Delete a dashboard, identified by the id from find_dashboard. This is destructive and cannot be undone, ' +
      'so it is confirm-first: call it with just the id first, read the returned confirmation message back to ' +
      'the user, and only call it again with confirmed=true once they explicitly say yes.',
    meta: { category: 'item-action', collection: 'dashboard', confirm: 'click' },
    parameters: {
      type: 'object',
      properties: {
        id: { type: 'string', description: 'The dashboard id (from find_dashboard).' },
        confirmed: {
          type: 'boolean',
          description: 'Set true only after the user has explicitly confirmed the deletion.',
        },
      },
      required: ['id'],
      additionalProperties: false,
    },
    execute: (args) => {
      const id = String(args.id ?? '')
      const dashboard = dashboards.find((d) => d.id === id)
      if (!dashboard) {
        return JSON.stringify({ error: `no dashboard with id "${id}" — call find_dashboard first` })
      }
      const name = dashboard.config?.name || dashboard.id
      if (args.confirmed !== true) {
        return JSON.stringify({
          needs_confirmation: true,
          message: `About to delete "${name}" — say yes to confirm`,
        })
      }
      deleteDashboard(dashboard.id)
      return JSON.stringify({ ok: true, deleted: name })
    },
  }

  // Douglas reads this back aloud, so it carries display names rather than

  // the codes in the URL (#1347 item 1).

  const names = useDisplayNames(customer)


  useVoiceRegistration({
    pageKey: `dashboards:${customer}:${selectedJob}`,
    pageContext: () =>
      `The DataViz list for ${names.customer}, job ${names.job(selectedJob)}. Items are grouped into company-level, ` +
      `your own, and colleagues'. You can create a new dashboard, find one by name, view/edit it, or delete it ` +
      `(delete requires confirmation).`,
    uiTools: [
      ...buildVoiceTools({
        actions: [{ name: 'create_dashboard', label: 'create a new dashboard', run: () => handleAddClick() }],
        collections: [
          voiceCollection<Dashboard>({
            name: 'dashboard',
            label: 'dashboard',
            list: () => dashboards,
            identify: (d) => ({ id: d.id, label: d.config?.name || d.id }),
            actions: [
              { name: 'view_dashboard', label: 'view', run: (d) => handleViewClick(d) },
              { name: 'edit_dashboard', label: 'edit', run: (d) => handleEditClick(d) },
            ],
          }),
        ],
      }),
      deleteDashboardTool,
    ],
  })

  // Resolve the Info popover's dashboard from the live list so an inline
  // description edit shows its committed value without reopening.
  const infoDashboard = infoPopover ? dashboards.find((d) => d.id === infoPopover.dashboardId) || null : null
  const infoCanOverwrite = canOverwriteDashboard(infoDashboard, userEmail, userCanPromote)
  const infoShareUrl = infoDashboard?.share_enabled && infoDashboard.share_id
    ? `${window.location.origin}/dashboard/${infoDashboard.share_id}`
    : null

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)' }}>
      <PageHeader
        title={scopeAll ? 'DataViz: All jobs' : selectedJob ? `DataViz: ${jobDisplayName}` : 'DataViz'}
        actions={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PageAgentButton
              pageKey="dashboards"
              customer={account.customer?.name || customer}
              open={helperOpen}
              onClick={() => setHelperOpen(o => !o)}
            />
            {/* #1162: the current job, or every job. A labelled switch
                (#1578 item 17): the unlabelled picker it replaces read as a
                second job picker and said nothing about what it did. */}
            <Tooltip describeChild title={scopeAll ? 'Showing DataViz items from every job' : `Showing DataViz items for ${jobDisplayName || 'the selected job'}`}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    color="info"
                    checked={scopeAll}
                    onChange={(e) => handleScopeChange(e.target.checked)}
                  />
                }
                label="Show all jobs"
                slotProps={{ typography: { variant: 'body2', color: 'text.secondary' } }}
                sx={{ mr: 1.5, ml: 0 }}
              />
            </Tooltip>
            <ToggleButtonGroup
              exclusive
              size="small"
              color="info"
              value={typeFilter}
              onChange={(_, next: string | null) => handleTypeFilterChange(next)}
              aria-label="Filter by type"
              sx={{ mr: 1.5 }}
            >
              <ToggleButton value="all" sx={{ textTransform: 'none', px: 1.25 }}>All</ToggleButton>
              {DATAVIZ_TYPES.map(t => (
                <ToggleButton key={t.id} value={t.id} sx={{ textTransform: 'none', px: 1.25 }}>{t.label}</ToggleButton>
              ))}
            </ToggleButtonGroup>
            <ActionButton icon={<PaletteIcon />} label="Edit Themes" onClick={() => setThemeManagerOpen(true)} />
            <ActionButton icon={<AddIcon />} label="New Dashboard" onClick={handleAddClick} />
          </Box>
        }
      />

      <Box sx={{ flex: 1, overflow: 'auto', backgroundColor: 'background.paper', p: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
        {!selectedJob && !scopeAll ? (
          <Alert severity="info">Select a job to see its DataViz items.</Alert>
        ) : listIsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : listIsError ? (
          <Alert severity="error">{listError instanceof Error ? listError.message : 'Unable to load DataViz items.'}</Alert>
        ) : (
          <>
            {/* Scope markers (1 Sep call): the tint and edge per tier live in
                TierSection's variant, shared with other tiered libraries. */}
            <TierSection
              title="Company-level"
              count={companyLevel.length}
              open={isTierOpen('company')}
              onToggle={() => toggleTier('company')}
              variant="company"
            >
              <TierTable dashboards={companyLevel} emptyLabel="No company-level DataViz items yet" rowProps={rowPropsFor} />
            </TierSection>

            <TierSection
              title="My DataViz"
              count={mine.length}
              open={isTierOpen('mine')}
              onToggle={() => toggleTier('mine')}
              variant="mine"
            >
              <TierTable dashboards={mine} emptyLabel="You haven't created any DataViz items for this job yet" rowProps={rowPropsFor} />
            </TierSection>

            <TierSection
              title="My colleagues' DataViz"
              count={colleagueCount}
              open={isTierOpen('colleagues')}
              onToggle={() => toggleTier('colleagues')}
              variant="colleagues"
              headerExtra={
                colleagueGroups.length > 0 && isTierOpen('colleagues') ? (
                  <TextField
                    size="small"
                    placeholder="Filter by colleague or name..."
                    value={colleagueFilter}
                    onChange={(e) => setColleagueFilter(e.target.value)}
                    sx={{ width: 260 }}
                  />
                ) : undefined
              }
            >
              {filteredColleagueGroups.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  {colleagueGroups.length === 0 ? 'No colleagues have DataViz items in this job yet' : 'No matches'}
                </Typography>
              ) : (
                filteredColleagueGroups.map((group) => (
                  <Box key={group.email} sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: 1, mb: 1, backgroundColor: 'background.paper' }}>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, cursor: 'pointer' }}
                      onClick={() => toggleColleague(group.email)}
                    >
                      <IconButton size="small" aria-label={`${expandedColleagues.has(group.email) ? 'Collapse' : 'Expand'} ${group.email}`}>
                        {expandedColleagues.has(group.email) ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                      </IconButton>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{group.email}</Typography>
                      <Typography variant="caption" color="text.secondary">({group.items.length})</Typography>
                    </Box>
                    <Collapse in={expandedColleagues.has(group.email)}>
                      <TierTable dashboards={group.items} emptyLabel="" rowProps={rowPropsFor} />
                    </Collapse>
                  </Box>
                ))
              )}
            </TierSection>
          </>
        )}
      </Box>

      <DeleteConfirmWindow
        open={Boolean(deleteTarget)}
        title="Delete Dashboard"
        message={`Are you sure you want to delete "${deleteTarget?.config?.name || deleteTarget?.id}"? This action cannot be undone.`}
        confirmWord="delete"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      {/* Info popover (#1179): description (still editable), job, created by,
          last modified by — the fields the dropped columns used to show. */}
      {infoPopover && infoDashboard && (
        <Popover
          open
          anchorEl={infoPopover.anchorEl}
          onClose={() => setInfoPopover(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Box sx={{ p: 2, width: 320, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary', fontWeight: 600 }}>
              Description
            </Typography>
            {infoCanOverwrite ? (
              <TextField
                size="small"
                fullWidth
                multiline
                minRows={2}
                placeholder="Add a description..."
                defaultValue={infoDashboard.config?.description || ''}
                onBlur={(e) => handleCommitDescription(infoDashboard, e.target.value)}
              />
            ) : (
              <Typography variant="body2" color={infoDashboard.config?.description ? 'text.primary' : 'text.disabled'}>
                {infoDashboard.config?.description || '—'}
              </Typography>
            )}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1.5, rowGap: 0.5, alignItems: 'baseline' }}>
              <Typography variant="caption" color="text.secondary">Job</Typography>
              <Typography variant="body2">{(infoDashboard.jobs?.length ? infoDashboard.jobs.map(resolveJobName).join(' · ') : (infoDashboard.job ? resolveJobName(infoDashboard.job) : '')) || '—'}</Typography>
              {/* #1257: two fields, two labels. Owner (`user`) holds the
                  authority; Created by (`created_by`) is whoever made it, and
                  the two diverge on a fork. The old `created_by || user` under
                  one label named someone with no say over the item. */}
              <Typography variant="caption" color="text.secondary">Owner</Typography>
              <Typography variant="body2" noWrap>{infoDashboard.user || '—'}</Typography>
              <Typography variant="caption" color="text.secondary">Created by</Typography>
              <Typography variant="body2" noWrap>{infoDashboard.created_by || '—'}</Typography>
              <Typography variant="caption" color="text.secondary">Last modified</Typography>
              <Typography variant="body2" noWrap>
                {infoDashboard.modified_by
                  ? `${infoDashboard.modified_by} · ${formatDate(infoDashboard.updated_at)}`
                  : '—'}
              </Typography>
            </Box>
            {infoShareUrl && (
              <Link href={infoShareUrl} target="_blank" rel="noopener" sx={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {`/dashboard/${infoDashboard.share_id}`}
                </Box>
                <OpenInNewIcon sx={{ fontSize: 13, flexShrink: 0 }} />
              </Link>
            )}
          </Box>
        </Popover>
      )}

      {sharePopover && (
        <DashboardSharePopover
          anchorEl={sharePopover.anchorEl}
          onClose={() => setSharePopover(null)}
          shareId={sharePopover.dashboard.share_id}
          shareAccessLevel={sharePopover.dashboard.share_access_level}
          shareEnabled={sharePopover.dashboard.share_enabled}
          isLoading={shareIsLoading}
          onShare={async (accessLevel, password) => {
            await shareDashboard(sharePopover.dashboard.id, accessLevel, password)
          }}
          onDisableShare={async () => {
            await disableShare(sharePopover.dashboard.id)
          }}
        />
      )}

      <ThemeManagerDialog
        open={themeManagerOpen}
        onClose={() => setThemeManagerOpen(false)}
        themes={brandThemes}
        loading={themesIsLoading}
        onSaveTheme={async (id, name, palette) => { await updateTheme(id, name, palette) }}
        onCreateTheme={async (name, palette) => { await createTheme(name, palette) }}
        onDeleteTheme={deleteTheme}
        currentUserEmail={userEmail}
        canPromote={userCanPromote}
        onPromoteTheme={promoteTheme}
        onDemoteTheme={demoteTheme}
        onLoadThemeHistory={loadThemeHistory}
        whoToAsk={whoToAsk}
      />

      <PageAgentDrawer
        pageKey="dashboards"
        customer={account.customer?.name || customer}
        job={selectedJob}
        open={helperOpen}
        onClose={() => setHelperOpen(false)}
      />
    </Box>
  )
}

export default Dashboards
